import { Monaco } from "@stedi/dls/MonacoEditor";
import { allBuiltInFunctions } from "@stedi/transformers-jsonata";
import type { IRange } from "monaco-editor";
import { generateFunctionsAutocompleteSuggestions, generateFunctionsSignatureHelp } from "./functionsAutocomplete";

// Adapted from https://github.com/jsonata-js/jsonata-exerciser
const registerJSONataLanguage = (monaco: Monaco) => {
  monaco.languages.register({ id: "jsonata" });

  // Register a tokens provider for the language
  monaco.languages.setMonarchTokensProvider("jsonata", {
    tokenizer: {
      root: [
        [/\/\*.*\*\//, "jsonata-comment"],
        [/'([^']*)'/, "jsonata-string"],
        [/"([^"]*)"/, "jsonata-string"],
        [/\$[a-zA-Z0-9_]*/, "jsonata-variable"],
        [/[a-zA-Z0-9_]+/, "jsonata-names"],
        { include: "@whitespace" },
      ],
      // Inspired by https://microsoft.github.io/monaco-editor/monarch.html
      comment: [
        [/[^/*]+/, "jsonata-comment"],
        [/\/\*/, "jsonata-comment", "@push"], // nested comment
        ["\\*/", "jsonata-comment", "@pop"],
        [/[/*]/, "jsonata-comment"],
      ],
      whitespace: [
        [/[ \t\r\n]+/, "white"],
        [/\/\*/, "jsonata-comment", "@comment"],
        [/\/\/.*$/, "jsonata-comment"],
      ],
    },
  });

  const brackets = [
    { open: "(", close: ")" },
    { open: "{", close: "}" },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
    { open: "`", close: "`" },
  ];
  monaco.languages.setLanguageConfiguration("jsonata", {
    comments: {
      blockComment: ["/*", "*/"],
    },
    brackets: [
      ["(", ")"],
      ["[", "]"],
      ["{", "}"],
    ],
    autoClosingPairs: brackets,
    surroundingPairs: brackets,
    indentationRules: {
      decreaseIndentPattern: /^((?!.*?\/\*).*\*\/)?\s*[}\])].*$/,
      increaseIndentPattern: /^((?!\/\/).)*(\{[^}"'`]*|\([^)"'`]*|\[[^\]"'`]*)$/,
    },
  });
};

// Adapted from https://microsoft.github.io/monaco-editor/playground.html#extending-language-services-completion-provider-example
const setupJSONataFunctionsAutocomplete = (monaco: Monaco) => {
  const jsonataAutocompleteProvider = monaco.languages.registerCompletionItemProvider("jsonata", {
    triggerCharacters: ["$"],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      return {
        suggestions: generateFunctionsAutocompleteSuggestions(range),
      };
    },
  });

  return jsonataAutocompleteProvider;
};

const setupJSONataFunctionsHover = (monaco: Monaco) => {
  const jsonataHoverProvider = monaco.languages.registerHoverProvider("jsonata", {
    provideHover: (model, position) => {
      const currentWord = model.getWordAtPosition(position);
      if (!currentWord) {
        return;
      }

      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: currentWord.startColumn,
        endColumn: currentWord.endColumn,
      };

      const matchingJSONataFunction = allBuiltInFunctions.find(
        (JSONataFunction) => JSONataFunction.label === currentWord.word,
      );
      if (!matchingJSONataFunction) {
        return;
      }

      return {
        range,
        contents: [
          { value: `**${matchingJSONataFunction.detail}**` },
          { value: matchingJSONataFunction.documentation, isTrusted: true },
        ],
      };
    },
  });

  return jsonataHoverProvider;
};

const setupJSONataFunctionsSignatureHelp = (monaco: Monaco) => {
  const jsonataHoverProvider = monaco.languages.registerSignatureHelpProvider("jsonata", {
    signatureHelpTriggerCharacters: ["(", ","],
    signatureHelpRetriggerCharacters: [")"],
    provideSignatureHelp: (model, position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 0,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const signatureHelp = generateFunctionsSignatureHelp(textUntilPosition);
      if (!signatureHelp) {
        return;
      }

      return { value: signatureHelp, dispose: () => {} };
    },
  });

  return jsonataHoverProvider;
};

interface SetupMonacoJSONataProps {
  monaco: Monaco;
}

const setupMonacoJSONata = ({ monaco }: SetupMonacoJSONataProps): VoidFunction => {
  registerJSONataLanguage(monaco);

  const jsonataFunctionsAutocompleteProvider = setupJSONataFunctionsAutocomplete(monaco);
  const jsonataFunctionsHoverProvider = setupJSONataFunctionsHover(monaco);
  const jsonataFunctionsSignatureHelpProvider = setupJSONataFunctionsSignatureHelp(monaco);

  monaco.languages.setLanguageConfiguration("jsonata", {
    // definition of a "word":
    //   a. (-?\d+\.\d\w*) - decimal with a dot separator (from the default monaco implementation)
    //   b. (\w*-+\w*) - word with a dash inside
    //   c. ("\w[^"]*")|("\w[^"]*$) - any characters in double-quotes, or with unfinished right-side quote, with at least one meaningful symbol at the beginning
    //   d. ([^`~!@#%^&*()-=+[{\]}\\|;:'",.<>/?\s]+) - any sequence of non-special chars (from the default monaco implementation)
    wordPattern: /(-?\d+\.\d\w*)|(\w*-+\w*)|("\w[^"]*")|("\w[^"]*$)|([^`~!@#%^&*()-=+[{\]}\\|;:'",.<>/?\s]+)/g,
  });

  const cleanup = () => {
    jsonataFunctionsAutocompleteProvider.dispose();
    jsonataFunctionsHoverProvider.dispose();
    jsonataFunctionsSignatureHelpProvider.dispose();
  };
  return cleanup;
};

const convertToSingleLine = (jsonataString: string) => jsonataString?.trim().replaceAll("\n", " ");

export { setupMonacoJSONata, convertToSingleLine };
